'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Play, Loader2, Check, X, CircleDot, Circle, AlertTriangle,
  HelpCircle, Square, History as HistoryIcon, Send,
} from 'lucide-react';

// ONE SCREEN per project. Boss: "I just want to start the project manually."
//
// The run is always visibly in ONE of four states — moving, waiting on you,
// done, failed. When it waits, the question is RIGHT HERE, not in another tab.
// Below it, the full history: every input and output, forever.

type Step = { key: string; label: string; state: 'done' | 'current' | 'pending' };
type Ask = { id: string; kind: string; question: string; why?: string; shown?: any };
type Buckets = { wall_s: number; build_s: number; fix_s: number; idle_s: number };
type Timers = { total: Buckets; run?: Buckets | null;
                active?: { since: string; step: string; bucket: 'build' | 'fix' } | null };
type St = { state: string; run?: string | null; step?: string | null; ask?: Ask | null;
            done?: string[]; steps?: Step[]; error?: string | null; timers?: Timers };
type Ev = { ts: string; type: string; step?: string; title?: string; data?: any };

const ICON: Record<string, string> = {
  run_start: '▶', run_end: '■', step_start: '·', step_end: '✓',
  ask: '?', answer: '!', evidence: '=', error: '✗', corrupt: '!',
};
const COLOR: Record<string, string> = {
  ask: 'text-amber-400', answer: 'text-cyan-400', step_end: 'text-emerald-400',
  error: 'text-red-400', corrupt: 'text-red-400', run_start: 'text-gray-400',
  run_end: 'text-gray-400',
};

// History is STORED in UTC (correct for records); it is SHOWN in the viewer's
// timezone. Boss in Toronto read '08-25 03:05' for something that happened at
// 23:05 on the 24th — a raw UTC slice is a wrong clock, not a timestamp.
// ── ASK CHAT — talk to Beast about the pending question, IN the question ──
// Boss (2026-09-01): "I need to be communicating to the beast when the window
// pops up … so I can ask the question about what I'm approving and get the
// answer inside the chat." Streams through the project's own gateway session
// (wakes it if hibernating), so the answer comes from the agent that did the
// work — and the exchange lands in that session's history too.
function AskChat({ slug, path, ask }: { slug: string; path: string; ask: any }) {
  const [q, setQ] = useState('');
  const [qa, setQa] = useState<{ q: string; a: string }[]>([]);
  const [waiting, setWaiting] = useState(false);
  const askReader = useRef<ReadableStreamDefaultReader | null>(null);
  const askFileRef = useRef<HTMLInputElement>(null);
  const stopAsk = async () => {
    try { await askReader.current?.cancel(); } catch { /* closed */ }
    fetch(`/api/gw/sessions/${encodeURIComponent(slug)}/abort`, { method: 'POST' }).catch(() => {});
    setWaiting(false);
  };
  const attachAsk = async (files: FileList | null) => {
    if (!files?.length) return;
    const landed: string[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 50 * 1024 * 1024) continue;
      const fd = new FormData(); fd.append('file', f);
      try {
        const r = await fetch(`/api/gw/sessions/${encodeURIComponent(slug)}/upload`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (r.ok) landed.push(d.path || f.name);
      } catch { /* skip */ }
    }
    if (landed.length) setQ(v => `${v}${v ? '\n' : ''}[attached: ${landed.join(', ')}] `);
  };
  const send = async () => {
    const question = q.trim();
    if (!question || waiting) return;
    setWaiting(true); setQ('');
    setQa(prev => [...prev, { q: question, a: '' }]);
    // The gateway 404s /chat unless a session for this slug is open + registered
    // (the pipeline never opens one on its own). /sessions/open is create-or-get,
    // so this is cheap after the first call.
    try {
      await fetch(`/api/gw/sessions/open`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, path }),
      });
    } catch { /* if open fails, the chat POST below surfaces the real error */ }
    const prompt =
      `Boss is looking at this pending pipeline question and wants to understand it before deciding. ` +
      `Answer his question in plain English, concisely. Do NOT do any work, do NOT change any files — explain only.\n\n` +
      `PENDING QUESTION:\n${JSON.stringify({ id: ask.id, kind: ask.kind, question: ask.question, why: ask.why, shown: ask.shown }, null, 1).slice(0, 6000)}\n\n` +
      `BOSS ASKS: ${question}`;
    try {
      const res = await fetch(`/api/gw/sessions/${encodeURIComponent(slug)}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: prompt }),
      });
      if (!res.ok || !res.body) throw new Error(`session chat returned ${res.status}`);
      const reader = res.body.getReader();
      askReader.current = reader;
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const frames = buf.split('\n\n');
        buf = frames.pop() || '';
        for (const f of frames) {
          const line = f.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') { try { await reader.cancel(); } catch {} break; }
          try {
            const e = JSON.parse(payload);
            if (e.type === 'text_delta' && typeof e.text === 'string') {
              setQa(prev => {
                const next = [...prev];
                next[next.length - 1] = { ...next[next.length - 1], a: next[next.length - 1].a + e.text };
                return next;
              });
            }
          } catch { /* keepalive / partial frame */ }
        }
      }
    } catch (e: any) {
      setQa(prev => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], a: `⚠️ ${e?.message || e}` };
        return next;
      });
    }
    setWaiting(false);
  };
  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-600">
        💬 Ask Beast about this before you decide
      </div>
      {qa.map((x, i) => (
        <div key={i} className="rounded-lg bg-sky-500/5 ring-1 ring-sky-500/20 p-2.5">
          <div className="text-[11px] font-medium text-sky-300">❓ {x.q}</div>
          <div className="mt-1 text-[12px] text-gray-200 whitespace-pre-wrap">
            {x.a || (waiting && i === qa.length - 1 ? 'thinking…' : '')}
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <input ref={askFileRef} type="file" multiple className="hidden"
               onChange={e => { attachAsk(e.target.files); e.target.value = ''; }} />
        <button onClick={() => askFileRef.current?.click()} title="Attach files (→ project uploads/)"
          className="px-2.5 rounded-lg bg-white/5 text-gray-400 hover:text-gray-200">📎</button>
        <textarea value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder='e.g. "what exactly am I approving here?" — Enter to ask, this does NOT answer the question above'
          rows={Math.min(5, Math.max(1, q.split('\n').length))}
          className="flex-1 bg-black/40 rounded-lg px-3 py-2 text-xs text-gray-100 resize-y
                     ring-1 ring-sky-500/20 focus:outline-none focus:ring-sky-500/50" />
        {waiting ? (
          <button onClick={stopAsk} title="Stop this answer"
            className="px-3 py-2 rounded-lg bg-red-500/20 text-red-300 text-xs">■</button>
        ) : (
          <button onClick={send} disabled={!q.trim()}
            className="px-3 py-2 rounded-lg bg-sky-500/20 text-sky-300 text-xs disabled:opacity-30">Ask</button>
        )}
      </div>
    </div>
  );
}

function fmtLocal(ts?: string): string {
  if (!ts) return '';
  const d = new Date(ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z');
  if (isNaN(d.getTime())) return ts.slice(5, 16).replace('T', ' ');
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function RunScreen({ slug, path, onBrief }:
                          { slug: string; path: string; onBrief?: () => void }) {
  const [st, setSt] = useState<St | null>(null);
  const [hist, setHist] = useState<Ev[]>([]);
  const [busy, setBusy] = useState(false);
  const [seeds, setSeeds] = useState<any[]>([]);
  const [seedQ, setSeedQ] = useState('');
  // Brief-before-run (Boss, 2026-08-31): a FULL run may not start until the
  // Stage -1 brief is complete. null = unknown (never block on unknown).
  const [briefOk, setBriefOk] = useState<boolean | null>(null);
  const [text, setText] = useState('');
  const [showHist, setShowHist] = useState(true);
  const [openPhase, setOpenPhase] = useState('');
  // stage → model map (factory/models.json via the API — same file run-loop
  // resolves per step, so the chip shows what the worker will actually set)
  const [models, setModels] = useState<Record<string, string>>({});
  useEffect(() => {
    fetch('/api/pipeline/models').then(r => r.json())
      .then(d => { if (d?.ok) setModels(d.stages || {}); })
      .catch(() => { /* no map → no chips; never guess a model */ });
  }, []);
  // Phase rows bundle FM stages; each maps to the bucket(s) run-loop uses.
  // 'Structure & design' spans stages 1+2 — show both when they differ.
  const phaseModel = (name: string): string | null => {
    const buckets: Record<string, string[]> = {
      'Research': ['0'], 'Structure & design': ['1', '2'],
      'Content & build': ['3'], 'Audit': ['audit'], 'Deploy': ['4'],
    };
    const ids = (buckets[name] || []).map(b => models[b]).filter(Boolean);
    if (!ids.length) return null;
    const short = [...new Set(ids.map(m => m.replace(/^claude-/, '')))];
    return short.join(' · ');
  };
  // Which bucket a fresh start walks. Remembered per project: auditing an
  // existing site is a repeated act, not a one-off.
  const [mode, setMode] = useState<'full' | 'audit' | 'deploy'>(() => {
    if (typeof window === 'undefined') return 'full';
    const v = window.localStorage.getItem(`beast.runmode.${path}`);
    return v === 'audit' || v === 'deploy' ? v : 'full';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem(`beast.runmode.${path}`, mode);
  }, [mode, path]);
  // A run already carries its mode; show the truth rather than a stale toggle.
  useEffect(() => {
    const m = (st as any)?.mode;
    if (m === 'audit' || m === 'deploy' || m === 'full') setMode(m);
  }, [(st as any)?.mode]);
  const [declared, setDeclared] = useState<Record<string, string>>({});
  const timer = useRef<any>(null);
  // Build clock: the server computes build/fix/idle up to the moment of the
  // poll; between polls we tick the active bucket locally so it reads like a
  // real stopwatch instead of jumping every 2s.
  const polledAt = useRef(0);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([
      fetch(`/api/pipeline/loop?path=${encodeURIComponent(path)}`).then(r => r.json()).catch(() => null),
      fetch(`/api/pipeline/loop?path=${encodeURIComponent(path)}&view=history&limit=400`)
        .then(r => r.json()).catch(() => null),
    ]);
    if (s) { setSt(s); polledAt.current = Date.now(); }
    if (h?.events) setHist(h.events);
    if (s?.ask?.kind === 'seeds') {
      const shown = s.ask.shown?.seeds || [];
      // Preserve decisions already made in this session across a poll tick.
      setSeeds(prev => prev.length === shown.length
        ? prev
        : shown.map((x: any) => ({ ...x, approved: x.approved ?? null })));
    }
  }, [path]);

  useEffect(() => { setSeeds([]); load(); }, [path, load]);

  useEffect(() => {
    fetch(`/api/pipeline/intake?path=${encodeURIComponent(path)}`)
      .then(r => r.json())
      .then(d => setBriefOk(d?.ok ? !!d.complete : null))
      .catch(() => setBriefOk(null));
  }, [path, st?.state]);

  // Fast poll while moving; SLOW poll in every other state. "Only poll while
  // running" left the banner frozen: a failed run restarted from outside this
  // tab (terminal, agent, another window) kept showing "Stopped — failed" for
  // hours while the pipeline was actually working — Boss read a healthy run
  // as dead. A 20s heartbeat catches external transitions without the noise
  // the original comment worried about.
  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(load, st?.state === 'running' ? 2000 : 20_000);
    return () => clearInterval(timer.current);
  }, [st?.state, load]);

  // 1s repaint while working or waiting, purely for the stopwatch display.
  useEffect(() => {
    if (st?.state !== 'running' && st?.state !== 'waiting') return;
    const t = setInterval(() => forceTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [st?.state]);

  const post = async (action: string, extra: any = {}) => {
    setBusy(true);
    const r = await fetch('/api/pipeline/loop', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, action, ...extra }),
    }).then(x => x.json()).catch(() => null);
    setBusy(false);
    if (r) setSt(r);
    if (action === 'answer') setSeeds([]);
    load();
  };

  // Group the flat step list into the phases the gates already define. The
  // boundary is a gate step: everything up to a gate is one phase, and the gate
  // itself is its own row so a hard stop can never be hidden inside a collapsed
  // group. Derived from the step list, not a second hardcoded copy of it.
  const phases = (() => {
    const out: { name: string; steps: Step[]; done: number; hasCurrent: boolean;
                 isGate: boolean }[] = [];
    let bucket: Step[] = [];
    const flush = (name: string) => {
      if (!bucket.length) return;
      out.push({ name, steps: bucket, done: bucket.filter(s => s.state === 'done').length,
                 hasCurrent: bucket.some(s => s.state === 'current'), isGate: false });
      bucket = [];
    };
    const NAMES = ['Research', 'Structure & design', 'Content & build', 'Audit', 'Deploy'];
    let n = 0;
    for (const s of st?.steps || []) {
      if (s.key.startsWith('gate')) {
        flush(NAMES[Math.min(n++, NAMES.length - 1)]);
        out.push({ name: s.label, steps: [s], done: s.state === 'done' ? 1 : 0,
                   hasCurrent: s.state === 'current', isGate: true });
      } else if (s.key.startsWith('audit_') && !bucket.some(b => b.key.startsWith('audit_'))
                 && bucket.length) {
        flush(NAMES[Math.min(n++, NAMES.length - 1)]);
        bucket.push(s);
      } else {
        bucket.push(s);
      }
    }
    flush(NAMES[Math.min(n, NAMES.length - 1)]);
    return out;
  })();

  const ask = st?.ask;
  const kept = seeds.filter(s => s.approved === true).length;
  const running = st?.state === 'running';
  const waiting = st?.state === 'waiting';

  // Stopwatch: server buckets + local drift since the last poll on the bucket
  // that is actively burning. Idle never ticks locally — idle growing in real
  // time while a worker runs would be a lie.
  const fmtDur = (s: number) => {
    s = Math.max(0, Math.round(s));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${String(m).padStart(2, '0')}m` : m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
  };
  const clock = (b: Buckets | null | undefined) => {
    if (!b) return null;
    const drift = (running && st?.timers?.active) || waiting ? (Date.now() - polledAt.current) / 1000 : 0;
    // While running the drift belongs to the active step's bucket; while
    // waiting on Boss it belongs to idle — that wait IS the idle he wants seen.
    const ab = waiting ? 'idle' : st?.timers?.active?.bucket;
    return {
      build: b.build_s + (ab === 'build' ? drift : 0),
      fix: b.fix_s + (ab === 'fix' ? drift : 0),
      idle: b.idle_s + (ab === 'idle' ? drift : 0),
      wall: b.wall_s + drift,
    };
  };
  const tTotal = clock(st?.timers?.total);
  const tRun = clock(st?.timers?.run);

  const banner = () => {
    if (running) return ['bg-cyan-500/10 ring-cyan-500/25 text-cyan-300', 'Working…'];
    if (waiting) return ['bg-amber-500/10 ring-amber-500/25 text-amber-300', 'Waiting on you'];
    if (st?.state === 'done') return ['bg-emerald-500/10 ring-emerald-500/25 text-emerald-300', 'Done'];
    if (st?.state === 'failed' || st?.state === 'error')
      return ['bg-red-500/10 ring-red-500/25 text-red-300', `Stopped — ${st.error || 'failed'}`];
    return ['bg-white/5 ring-white/10 text-gray-400', 'Not started'];
  };
  const [bcls, btext] = banner();
  // The brief block only applies to a FRESH full run. A run that already passed
  // its brief step (st.done has 'brief') is a RESUME — and this tab's 10-section
  // completeness check is stricter than the server's step_brief (niche present
  // = done, empty sections merely noted). The stricter client gate greyed out
  // Retry on wastewaterteatment 20 steps in, with 'customer/proof/facts/
  // designPath' empty on a brief the server had accepted (2026-09-14).
  const briefBlocks = mode === 'full' && briefOk === false && !(st?.done || []).includes('brief');

  return (
    <div className="flex flex-col gap-4">
      {/* ── the one control ─────────────────────────────── */}
      {/* No brief → no full run. The server enforces this too (step_brief
          fails on a missing/hollow brief); this panel says it BEFORE the click
          instead of after. audit/deploy run against an existing site and need
          no brief. */}
      {briefBlocks && !running && !waiting && (
        <div className="rounded-xl bg-cyan-500/[0.07] ring-1 ring-cyan-500/25 p-4
                        flex items-center gap-3">
          <div className="text-sm text-cyan-200">
            The brief comes first — the run derives everything from it.
          </div>
          <button onClick={onBrief}
            className="px-3 py-1.5 rounded-lg text-xs bg-cyan-500/20 text-cyan-300
                       hover:bg-cyan-500/30">
            Fill the brief →
          </button>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => post('start', mode === 'full' ? {} : { only: mode })}
          disabled={busy || running || waiting || briefBlocks}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                     bg-amber-500/20 text-amber-300 hover:bg-amber-500/30
                     disabled:opacity-30 disabled:cursor-not-allowed">
          {busy || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {st?.state === 'done' ? 'Run again' : st?.state === 'failed' ? 'Retry' : 'Start'}
          {mode !== 'full' && <span className="opacity-70">· {mode} only</span>}
        </button>

        {/* Which bucket to walk. Boss: "run other existing project website
            through pipeline but only let say audit". A resume keeps the mode
            it started in, so this only bites on a fresh start. */}
        {!running && !waiting && (
          <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1 ring-1 ring-white/10">
            {(['full', 'audit', 'deploy'] as const).map(mkey => (
              <button key={mkey} onClick={() => setMode(mkey)} disabled={busy}
                title={mkey === 'audit'
                  ? 'Inspect an already-built site: 35 audit steps, no building, no deploy'
                  : mkey === 'deploy' ? 'Ship an already-audited site'
                  : 'Every step, research through deploy'}
                className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${
                  mode === mkey ? 'bg-amber-500/20 text-amber-300'
                                : 'text-gray-500 hover:text-gray-300'}`}>
                {mkey}
              </button>
            ))}
          </div>
        )}
        {(running || waiting) && (
          <button onClick={() => post('abort')} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs
                       bg-white/5 text-gray-400 hover:bg-white/10">
            <Square className="h-3 w-3" /> Stop
          </button>
        )}
        <div className={`flex-1 min-w-[200px] rounded-xl ring-1 px-3 py-2 text-xs ${bcls}`}>
          {btext}
        </div>
      </div>

      {/* ── build clock ──────────────────────────────────────
          Boss: "clean time for building from beginning till the ship, and how
          much time went to fixing errors and Beast being idle." Build ticks
          only while a first-attempt step is executing; retries tick Fix; idle
          is everything else (asks, crashes, nights). */}
      {tTotal && (
        <div className="flex items-center gap-4 flex-wrap rounded-xl bg-black/30 ring-1 ring-white/10 px-4 py-2.5 text-xs font-mono">
          <span className="flex items-center gap-1.5 text-emerald-300">
            <span className={`h-1.5 w-1.5 rounded-full bg-emerald-400 ${running && st?.timers?.active?.bucket === 'build' ? 'animate-pulse' : 'opacity-30'}`} />
            Build {fmtDur(tTotal.build)}
          </span>
          <span className="flex items-center gap-1.5 text-amber-300">
            <span className={`h-1.5 w-1.5 rounded-full bg-amber-400 ${running && st?.timers?.active?.bucket === 'fix' ? 'animate-pulse' : 'opacity-30'}`} />
            Fix {fmtDur(tTotal.fix)}
          </span>
          <span className="flex items-center gap-1.5 text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-500 opacity-30" />
            Idle {fmtDur(tTotal.idle)}
          </span>
          <span className="text-gray-600">wall {fmtDur(tTotal.wall)}</span>
          {tRun && (
            <span className="ml-auto text-gray-500">
              this run — build {fmtDur(tRun.build)} · fix {fmtDur(tRun.fix)} · idle {fmtDur(tRun.idle)}
            </span>
          )}
        </div>
      )}

      {/* ── progress, GROUPED ───────────────────────────────
          166 steps as a flat list is a wall nobody can read: the gates vanish
          and you cannot tell where you are. Steps collapse into the phases the
          gates already define, and only the phase you are IN is expanded. */}
      <div className="rounded-xl bg-black/30 ring-1 ring-white/10 divide-y divide-white/5">
        {phases.map(ph => {
          const open = openPhase === ph.name || ph.hasCurrent;
          const allDone = ph.steps.length > 0 && ph.done === ph.steps.length;
          return (
            <div key={ph.name}>
              <button
                onClick={() => setOpenPhase(openPhase === ph.name ? '' : ph.name)}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/[0.03] text-left">
                {allDone
                  ? <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                  : ph.hasCurrent
                    ? <CircleDot className={`h-4 w-4 text-amber-400 shrink-0 ${running ? 'animate-pulse' : ''}`} />
                    : <Circle className="h-4 w-4 text-gray-700 shrink-0" />}
                <span className={`text-sm flex-1 ${ph.isGate ? 'text-amber-300 font-medium'
                  : ph.hasCurrent ? 'text-gray-100' : allDone ? 'text-gray-400' : 'text-gray-600'}`}>
                  {ph.isGate ? `⏸ ${ph.name}` : ph.name}
                </span>
                {!ph.isGate && phaseModel(ph.name) && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ring-1 shrink-0 ${
                    /fable/.test(phaseModel(ph.name)!)
                      ? 'bg-fuchsia-500/10 text-fuchsia-300 ring-fuchsia-500/25'
                      : 'bg-white/5 text-gray-500 ring-white/10'}`}>
                    {phaseModel(ph.name)}
                  </span>
                )}
                {!ph.isGate && (
                  <span className="text-[10px] text-gray-600 tabular-nums shrink-0">
                    {ph.done}/{ph.steps.length}
                  </span>
                )}
              </button>
              {open && !ph.isGate && (
                <div className="bg-black/20 border-t border-white/5 max-h-64 overflow-y-auto">
                  {ph.steps.map(s => (
                    <div key={s.key} className="flex items-center gap-2 pl-9 pr-3 py-1">
                      {s.state === 'done' ? <Check className="h-3 w-3 text-emerald-400/70 shrink-0" />
                        : s.state === 'current' ? <CircleDot className="h-3 w-3 text-amber-400 shrink-0" />
                          : <Circle className="h-3 w-3 text-gray-800 shrink-0" />}
                      <span className={`text-[11px] ${s.state === 'pending' ? 'text-gray-600'
                        : s.state === 'done' ? 'text-gray-500' : 'text-gray-100'}`}>{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!st?.steps?.length && <div className="px-3 py-3 text-xs text-gray-600">Press Start.</div>}
      </div>

      {/* ── THE QUESTION, inline ────────────────────────── */}
      {waiting && ask && (
        <div className="rounded-xl bg-amber-500/[0.07] ring-1 ring-amber-500/30 p-4 flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <HelpCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-amber-100 font-medium">{ask.question}</div>
              {ask.why && <div className="text-[11px] text-amber-200/60 mt-1 leading-relaxed">{ask.why}</div>}
            </div>
          </div>

          {ask.kind === 'seeds' && (
            <>
              {/* Cost is 2 API calls per seed per language, so 90 seeds is a
                  few dollars, not a budget event. Quantity is NOT the risk —
                  AMBIGUITY is. Show the real cost and say what to actually cut,
                  instead of nagging toward an arbitrary count. */}
              <div className="text-[11px] text-amber-200/70 leading-relaxed">
                Keeping more seeds is cheap: <b>2 API calls per seed per language</b>,
                and off-niche results get filtered out later anyway. Cut a seed only if it
                could mean <b>something else</b> in search — that is the one that poisons
                the corpus. Quantity is not the risk.
              </div>
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <span className="text-emerald-400">{kept} keep</span> ·
                <span>{seeds.length - kept} not kept</span>
                <span className="text-gray-600">· ~{kept * 2} calls/language</span>
                <div className="flex-1" />
                <button onClick={() => setSeeds(s => s.map(x => ({ ...x, approved: true })))}
                  className="px-2 py-0.5 rounded bg-white/5 hover:bg-white/10">all</button>
                <button onClick={() => setSeeds(s => s.map(x => ({ ...x, approved: null })))}
                  className="px-2 py-0.5 rounded bg-white/5 hover:bg-white/10">none</button>
              </div>
              <div className="max-h-[340px] overflow-y-auto rounded-lg bg-black/40 divide-y divide-white/5">
                {seeds.map((s, i) => (
                  <div key={s.seed} className={`flex items-center gap-2 px-2.5 py-1.5
                      ${s.approved === true ? 'bg-emerald-500/5' : s.approved === false ? 'opacity-40' : ''}`}>
                    <button onClick={() => setSeeds(v => v.map((x, n) => n === i ? { ...x, approved: x.approved === true ? null : true } : x))}
                      className={`h-5 w-5 rounded flex items-center justify-center shrink-0
                        ${s.approved === true ? 'bg-emerald-500/25 text-emerald-300' : 'bg-white/5 text-gray-600 hover:text-emerald-300'}`}>
                      <Check className="h-3 w-3" />
                    </button>
                    <button onClick={() => setSeeds(v => v.map((x, n) => n === i ? { ...x, approved: x.approved === false ? null : false } : x))}
                      className={`h-5 w-5 rounded flex items-center justify-center shrink-0
                        ${s.approved === false ? 'bg-red-500/25 text-red-300' : 'bg-white/5 text-gray-600 hover:text-red-300'}`}>
                      <X className="h-3 w-3" />
                    </button>
                    <span className={`text-xs flex-1 ${s.approved === false ? 'line-through text-gray-600' : 'text-gray-200'}`}>{s.seed}</span>
                    <span className="text-[10px] text-gray-600 hidden sm:block truncate max-w-[40%]">{s.reason}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => post('answer', {
                  answer: { seeds: seeds.map(s => ({ ...s, approved: s.approved === true })),
                            _summary: `kept ${kept}, cut ${seeds.length - kept}` },
                })}
                disabled={busy || kept === 0}
                className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                           bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30">
                <Send className="h-3.5 w-3.5" /> Continue with {kept} seed{kept === 1 ? '' : 's'}
              </button>
              {kept === seeds.length && seeds.length > 0 && (
                <span className="text-[11px] text-gray-600">
                  All {seeds.length} kept — that is a valid choice, not a lazy one.
                </span>
              )}
              {kept === 0 && <span className="text-[11px] text-gray-600">Keep at least one seed to continue — or reject the whole set.</span>}

              {/* REJECT ALL — Boss, 2026-09-19: "There's no way to reject in the
                  fucking pipeline... I will be rejecting, you should be like a
                  button, reject all." A bad BRIEF produces a bad seed set, and
                  the answer to that is not cutting 22 checkboxes one at a time.
                  This parks the run, records why, and asks for the fix. */}
              <div className="flex items-center gap-2 pt-1 border-t border-white/5">
                <button
                  onClick={() => post('answer', {
                    answer: {
                      rejected: true,
                      reason: seedQ.trim() || 'all seeds rejected',
                      _summary: `REJECTED all ${seeds.length} seeds`,
                    },
                  })}
                  disabled={busy || seeds.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-red-500/15 text-red-300 hover:bg-red-500/30 disabled:opacity-30">
                  <X className="h-3.5 w-3.5" /> Reject all {seeds.length}
                </button>
                <span className="text-[11px] text-gray-600">
                  One click, no questions. Beast works out why they were dead, fixes the
                  brief, and comes back with a new set. Nothing is spent.
                </span>
              </div>

              {/* WHAT THE REBUILD FOUND — shown because Boss rejected without
                  explaining, so the diagnosis is Beast reporting back to him,
                  not Beast asking him anything. */}
              {ask.shown?._rebuild && (
                <div className="rounded-lg bg-black/30 ring-1 ring-white/10 px-3 py-2.5 flex flex-col gap-1.5">
                  <div className="text-[11px] text-amber-300">
                    Rebuild #{ask.shown._rebuild.attempt} — why the last set was dead
                  </div>
                  <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {ask.shown._rebuild.diagnosis}
                  </div>
                  {(ask.shown._rebuild.fixed || []).length > 0 && (
                    <ul className="text-[11px] text-gray-400 list-disc pl-4">
                      {(ask.shown._rebuild.fixed as string[]).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  )}
                  {(ask.shown._rebuild.unchanged_from_rejected || []).length > 0 && (
                    <div className="text-[11px] text-red-300/80">
                      {ask.shown._rebuild.unchanged_from_rejected.length} seed(s) survived unchanged
                      from the set you rejected — check those first.
                    </div>
                  )}
                </div>
              )}

              {/* DISCUSSION — asking about the seeds is not deciding them (Boss,
                  2026-08-31). A question posts {question} instead of {seeds};
                  the loop answers via a one-shot agent and re-asks with the
                  thread in shown._discussion. */}
              {(ask.shown?._discussion || []).length > 0 && (
                <div className="rounded-lg bg-black/30 divide-y divide-white/5">
                  {(ask.shown._discussion as any[]).map((t, i) => (
                    <div key={i} className="px-3 py-2 flex flex-col gap-1">
                      <div className="text-[11px] text-cyan-300">You: {t.q}</div>
                      <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap">{t.a}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  value={seedQ}
                  onChange={e => setSeedQ(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey && seedQ.trim()) {
                      e.preventDefault();
                      post('answer', { answer: { question: seedQ.trim(), _summary: `question: ${seedQ.trim().slice(0, 80)}` } });
                      setSeedQ('');
                    }
                  }}
                  rows={2}
                  placeholder="Don't understand a seed? Ask about it — asking never approves anything."
                  className="flex-1 rounded-lg bg-black/40 ring-1 ring-white/10 px-3 py-2 text-xs
                             text-gray-200 placeholder:text-gray-600 outline-none focus:ring-cyan-500/40 resize-y"
                />
                <button
                  onClick={() => {
                    if (!seedQ.trim()) return;
                    post('answer', { answer: { question: seedQ.trim(), _summary: `question: ${seedQ.trim().slice(0, 80)}` } });
                    setSeedQ('');
                  }}
                  disabled={busy || !seedQ.trim()}
                  className="px-3 py-2 rounded-lg text-xs bg-cyan-500/15 text-cyan-300
                             hover:bg-cyan-500/25 disabled:opacity-30 shrink-0">
                  Ask
                </button>
              </div>
            </>
          )}

          {ask.kind === 'review' && (
            <>
              {ask.shown?.flags && (ask.shown.flags.pct_volume > 0 || ask.shown.flags.rows > 0) && (
                <div className={`rounded-lg p-3 ring-1 ${ask.shown.flags.pct_volume >= 10
                  ? 'bg-red-500/10 ring-red-500/30' : 'bg-white/5 ring-white/10'}`}>
                  {/* Row count LOOKS harmless and volume is what actually decides
                      what gets built. Show both side by side so the gap is the
                      first thing seen, not a footnote. */}
                  <div className="flex gap-4 flex-wrap text-[11px] mb-2">
                    <span className="text-gray-400">
                      off-topic rows: <b className="text-gray-200">{ask.shown.flags.pct_rows}%</b>
                      <span className="text-gray-600"> ({ask.shown.flags.rows})</span>
                    </span>
                    <span className={ask.shown.flags.pct_volume >= 10 ? 'text-red-300' : 'text-gray-400'}>
                      off-topic SEARCH VOLUME: <b>{ask.shown.flags.pct_volume}%</b>
                    </span>
                  </div>
                  {(ask.shown.flags.worst || []).length > 0 && (
                    <div className="text-[10px] text-gray-500 font-mono leading-relaxed">
                      {(ask.shown.flags.worst || []).slice(0, 6).map((w: any) => (
                        <div key={w.keyword}>
                          <span className="text-gray-600">{String(w.volume).padStart(6)}</span> · {w.keyword}
                        </div>
                      ))}
                    </div>
                  )}
                  {(ask.shown.flags.suspicious || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/5">
                      <div className="text-[10px] uppercase tracking-wider text-amber-400/70 mb-1">
                        highest-volume short terms — check these mean YOUR business
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(ask.shown.flags.suspicious || []).map((w: any) => (
                          <span key={w.keyword} className="text-[10px] font-mono px-1.5 py-0.5
                                                           rounded bg-white/5 text-gray-300">
                            {w.keyword} <span className="text-gray-600">{w.volume}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="max-h-[420px] overflow-y-auto rounded-lg bg-black/50 p-3
                              text-[11px] text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                {ask.shown?.summary_md || '(no summary produced)'}
              </div>
              {ask.shown?.file && (
                <div className="text-[10px] text-gray-600 break-all">{ask.shown.file}</div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => post('answer', { answer: { approved: true, text,
                    _summary: 'corpus approved' + (text ? ` — ${text.slice(0, 60)}` : '') } })}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30">
                  <Check className="h-3.5 w-3.5" /> Looks right — continue
                </button>
                <button
                  onClick={() => post('answer', { answer: { approved: false, text,
                    _summary: 'corpus REJECTED' + (text ? ` — ${text.slice(0, 60)}` : '') } })}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-30">
                  <X className="h-3.5 w-3.5" /> Wrong — stop
                </button>
                <input value={text} onChange={e => setText(e.target.value)}
                  placeholder="note (optional) — goes on the record"
                  className="flex-1 min-w-[180px] bg-black/40 rounded-lg px-3 py-1.5 text-xs
                             text-gray-100 ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
              </div>
            </>
          )}

          {/* STUCK — a step that failed the same way twice and parked itself.
              Boss 2026-10-16 saw "Unrecognised question type 'stuck' — answering as
              free text": the run-loop raises this ask but the UI had no renderer,
              so the one card that should offer ACTIONS degraded to a bare textbox.
              Same class as the gate card that showed a scoreboard instead of the
              work: a question the UI could not speak. Answering ANY payload clears
              this step's infra budgets and resumes it (cmd_answer, qid .stuck). */}
          {ask.kind === 'stuck' && (
            <>
              <div className="rounded-lg bg-red-500/10 ring-1 ring-red-500/30 p-3 space-y-2">
                <div className="text-[12px] text-red-300 font-medium">
                  The step stopped itself after two identical failures
                </div>
                <div className="text-[11px] text-gray-300 whitespace-pre-wrap font-mono">
                  {String(ask.shown?.error ?? 'no error recorded')}
                </div>
                <div className="flex gap-3 text-[10px] text-gray-500">
                  <span>infra attempts: {String(ask.shown?.infra_attempts ?? '?')}</span>
                  <span>auto-restarts: {String(ask.shown?.auto_restarts ?? '?')}</span>
                </div>
                <div className="text-[11px] text-gray-400">
                  Retrying unchanged fails identically. Fix the cause first — then resume.
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => post('answer', { answer: { resume: true, text,
                    _summary: 'stuck RESUMED' + (text ? ` — ${text.slice(0, 60)}` : '') } })}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30">
                  <Check className="h-3.5 w-3.5" /> Cause fixed — resume this step
                </button>
                <button
                  onClick={() => post('abort', {})}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-30">
                  <X className="h-3.5 w-3.5" /> Stop the run
                </button>
                <input value={text} onChange={e => setText(e.target.value)}
                  placeholder="what you changed (optional) — goes on the record"
                  className="flex-1 min-w-[180px] bg-black/40 rounded-lg px-3 py-1.5 text-xs
                             text-gray-100 ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
              </div>
            </>
          )}

          {ask.kind === 'declare' && (
            <>
              <div className="flex flex-col gap-2">
                {(ask.shown?.needs || []).map((n: any) => (
                  <div key={n.id} className="rounded-lg bg-black/40 p-2.5 flex flex-col gap-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] font-mono text-cyan-400/80 shrink-0 mt-0.5">{n.id}</span>
                      <span className="text-xs text-gray-100">{n.ask}</span>
                    </div>
                    {n.why && <div className="text-[10px] text-gray-600 pl-1">{n.why}</div>}
                    <input
                      value={declared[n.id] || ''}
                      onChange={e => setDeclared(d => ({ ...d, [n.id]: e.target.value }))}
                      placeholder="your answer…"
                      className="bg-black/50 rounded px-2.5 py-1.5 text-xs text-gray-100
                                 ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
                  </div>
                ))}
              </div>
              {(() => {
                const needs = ask.shown?.needs || [];
                const unanswered = needs.filter((n: any) => !(declared[n.id] || '').trim()).length;
                return (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => { post('answer', { answer: {
                        declared: Object.fromEntries(needs.map((n: any) =>
                          [`${n.id} — ${n.ask}`, declared[n.id]])),
                        _summary: `declared ${needs.length} item${needs.length === 1 ? '' : 's'}` } });
                        setDeclared({}); }}
                      disabled={busy || unanswered > 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                                 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30">
                      <Send className="h-3.5 w-3.5" /> Continue
                    </button>
                    {unanswered > 0 && (
                      <span className="text-[11px] text-gray-600">
                        {unanswered} still unanswered — the run cannot proceed without all of them.
                      </span>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {ask.kind === 'gate' && (
            <>
              {/* Boss: "What do I approve? I need to SEE what I am approving."
                  The thing itself, first — tallies second.

                  2026-10-15, angry and right: the research gate showed him
                  "121 proven / 0 failing" and not one keyword. A check count
                  answers "did the machine run?"; he is answering "is this work
                  right?". The deliverable (run-loop _gate_deliverable) renders
                  FIRST, in full, and the tally is demoted to a footnote. */}
              {ask.shown?.deliverable?._missing && (
                <div className="rounded-lg bg-red-500/10 ring-1 ring-red-500/30 p-3 text-[12px] text-red-300">
                  Nothing to show: {ask.shown.deliverable._missing}
                  <div className="text-gray-400 mt-1">Do not approve — the work product is missing.</div>
                </div>
              )}
              {ask.shown?.deliverable?.corpus && (
                <div className="space-y-2.5">
                  <div className="text-[12px] text-gray-300">{ask.shown.deliverable.what_this_is}</div>
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="px-2 py-1 rounded bg-cyan-500/15 text-cyan-300">
                      {ask.shown.deliverable.corpus.keywords?.toLocaleString()} keywords
                    </span>
                    <span className="px-2 py-1 rounded bg-cyan-500/15 text-cyan-300">
                      {ask.shown.deliverable.corpus.total_volume_per_month?.toLocaleString()} searches/mo
                    </span>
                    {ask.shown.deliverable.site_plan ? (
                      <span className="px-2 py-1 rounded bg-white/5 text-gray-300">
                        {ask.shown.deliverable.site_plan.total_pages_full_matrix?.toLocaleString()} pages planned
                        {' · wave 1 = '}{ask.shown.deliverable.site_plan.first_wave_pages}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded bg-white/5 text-gray-300">
                        {ask.shown.deliverable.keyword_page_count} keyword pages
                      </span>
                    )}
                    <span className="px-2 py-1 rounded bg-white/5 text-gray-400">
                      {ask.shown.deliverable.corpus.provider} · {ask.shown.deliverable.corpus.market}
                      {' · '}{ask.shown.deliverable.approved_seed_count} seeds
                      {' · '}{ask.shown.deliverable.spend?.api_calls} API calls
                    </span>
                  </div>

                  {(ask.shown.deliverable.ambiguity_warnings || []).map((w: string, i: number) => (
                    <div key={i} className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 p-2.5
                                            text-[11px] text-amber-200 whitespace-pre-wrap">
                      ⚠ two-meaning risk — read before approving: {w}
                    </div>
                  ))}

                  {ask.shown.deliverable.site_plan && (
                    <div className="rounded-lg bg-black/40 p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">
                        the site this builds — {ask.shown.deliverable.site_plan.formula}
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px] mb-2">
                        {Object.entries(ask.shown.deliverable.site_plan.inventory || {}).map(
                          ([k, v]) => (
                          <span key={k} className="px-2 py-0.5 rounded bg-white/5 text-gray-300">
                            {String(v)} {k}
                          </span>
                        ))}
                        <span className="px-2 py-0.5 rounded bg-white/5 text-gray-300">
                          {ask.shown.deliverable.site_plan.keyword_landing_pages} keyword landing pages
                        </span>
                      </div>
                      <div className="space-y-1">
                        {(ask.shown.deliverable.site_plan.waves || []).map(
                          (w: {wave: string; pages: number; what: string}) => (
                          <div key={w.wave} className="text-[11px] flex gap-2">
                            <span className="text-cyan-300 font-mono shrink-0">
                              {w.wave.replace('_', ' ')}: {w.pages?.toLocaleString()}
                            </span>
                            <span className="text-gray-400">{w.what}</span>
                          </div>
                        ))}
                      </div>
                      {ask.shown.deliverable.site_plan.thin_content_band && (
                        <div className="text-[10px] text-gray-500 mt-1.5">
                          {ask.shown.deliverable.site_plan.thin_content_band}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <div className="rounded-lg bg-black/40 p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">
                        top keywords — searches per month
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-0.5">
                        {(ask.shown.deliverable.top_keywords || []).map((k: {keyword: string; per_month: number}) => (
                          <div key={k.keyword} className="flex justify-between gap-3 text-[11px]">
                            <span className="text-gray-200 truncate">{k.keyword}</span>
                            <span className="text-cyan-300 font-mono shrink-0">{k.per_month?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-lg bg-black/40 p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">
                        keyword landing pages (one slice of the site)
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {(ask.shown.deliverable.pages_this_buys || []).map(
                          (p: {slug: string; name: string; primary_keyword: string; per_month: number}) => (
                          <div key={p.slug} className="text-[11px]">
                            <div className="flex justify-between gap-3">
                              <span className="text-gray-200 truncate">/{p.slug}</span>
                              <span className="text-cyan-300 font-mono shrink-0">{p.per_month?.toLocaleString()}</span>
                            </div>
                            <div className="text-[10px] text-gray-500 truncate">← {p.primary_keyword}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {ask.shown.deliverable.summary_markdown && (
                    <details className="rounded-lg bg-black/40 p-2.5">
                      <summary className="text-[11px] text-cyan-300 cursor-pointer">
                        Read the full research summary
                      </summary>
                      <pre className="mt-2 text-[10px] text-gray-300 whitespace-pre-wrap
                                      max-h-80 overflow-y-auto">{ask.shown.deliverable.summary_markdown}</pre>
                    </details>
                  )}
                  <div className="text-[10px] text-gray-600">
                    source: {(ask.shown.deliverable.read_the_source || []).filter(Boolean).join(' · ')}
                  </div>
                </div>
              )}
              {ask.shown?.preview && (
                ask.shown.preview.ok ? (
                  <div className="space-y-2">
                    {ask.shown.preview.url && (
                      <a href={ask.shown.preview.url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg
                                    bg-cyan-500/15 text-cyan-300 text-[12px] font-medium
                                    ring-1 ring-cyan-500/30 hover:bg-cyan-500/25">
                        ↗ Open the site live — this is what you are approving
                      </a>
                    )}
                    <div className="flex gap-3 flex-wrap">
                      {(ask.shown.preview.shots || []).map((s: string) => (
                        <a key={s} href={`/api/pipeline/preview-img?file=${encodeURIComponent(s)}`}
                           target="_blank" rel="noreferrer" className="block">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={`/api/pipeline/preview-img?file=${encodeURIComponent(s)}`}
                               alt={s.includes('mobile') ? 'mobile preview' : 'desktop preview'}
                               className={`rounded-lg ring-1 ring-white/10 object-cover object-top
                                           ${s.includes('mobile') ? 'h-64 w-auto' : 'h-64 w-auto max-w-[420px]'}`} />
                          <div className="text-[10px] text-gray-500 mt-1">
                            {s.includes('mobile') ? 'mobile · 390px webkit' : 'desktop · 1280px'}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-red-500/10 ring-1 ring-red-500/30 p-3 text-[11px] text-red-300">
                    Preview failed — the site does not run right now: {ask.shown.preview.error}
                    <div className="text-gray-400 mt-1">Approving without seeing it is approving blind.</div>
                  </div>
                )
              )}
              <div className="flex gap-2 flex-wrap text-[11px]">
                <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300">
                  {ask.shown?.tally?.pass ?? 0} proven
                </span>
                <span className="px-2 py-1 rounded bg-red-500/15 text-red-300">
                  {ask.shown?.tally?.fail ?? 0} failing
                </span>
                <span className="px-2 py-1 rounded bg-white/5 text-gray-400">
                  {ask.shown?.tally?.pending ?? 0} unproven
                </span>
                {ask.shown?.coverage && (
                  <span className={`px-2 py-1 rounded ${ask.shown.coverage.pct < 90 ? 'bg-amber-500/15 text-amber-300' : 'bg-white/5 text-gray-400'}`}>
                    {ask.shown.coverage.recorded} / {ask.shown.coverage.index_total} checks ({ask.shown.coverage.pct}%)
                    {' · modes: '}{(ask.shown.coverage.modes_seen || []).join(', ') || 'none'}
                    {!ask.shown.coverage.build_ran && ' · BUILD NEVER RAN'}
                  </span>
                )}
              </div>
              {(ask.shown?.unproven || []).length > 0 && (
                <div className="rounded-lg bg-black/40 p-2.5 max-h-40 overflow-y-auto">
                  <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">
                    not proven — approving ships these unverified
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(ask.shown.unproven || []).map((id: string) => (
                      <span key={id} className="text-[10px] font-mono px-1.5 py-0.5 rounded
                                                bg-white/5 text-gray-400">{id}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => post('answer', { answer: { approved: true, text,
                    _summary: 'gate APPROVED' + (text ? ` — ${text.slice(0, 60)}` : '') } })}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30">
                  <Check className="h-3.5 w-3.5" /> Approve — continue
                </button>
                <button
                  onClick={() => post('answer', { answer: { approved: false, text,
                    _summary: 'gate HELD' + (text ? ` — ${text.slice(0, 60)}` : '') } })}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                             bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-30">
                  <X className="h-3.5 w-3.5" /> Hold — stop here
                </button>
                <input value={text} onChange={e => setText(e.target.value)}
                  placeholder="note (optional) — goes on the record"
                  className="flex-1 min-w-[180px] bg-black/40 rounded-lg px-3 py-1.5 text-xs
                             text-gray-100 ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
              </div>
            </>
          )}

          {/* FALLBACK. A question the UI does not recognise must never be a dead
              end: the run would sit in 'waiting' with no way to answer it. Show
              the raw payload and take free text. */}
          {!['seeds', 'review', 'chat', 'declare', 'gate', 'stuck'].includes(ask.kind) && (
            <>
              <div className="rounded-lg bg-black/50 p-2.5 text-[10px] font-mono
                              text-gray-400 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {JSON.stringify(ask.shown ?? {}, null, 2).slice(0, 3000)}
              </div>
              <div className="text-[11px] text-amber-300/80">
                Unrecognised question type &quot;{ask.kind}&quot; — answering as free text.
              </div>
              <div className="flex gap-2">
                <input value={text} onChange={e => setText(e.target.value)}
                  placeholder="your answer…"
                  className="flex-1 bg-black/40 rounded-lg px-3 py-2 text-sm text-gray-100
                             ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
                <button onClick={() => { post('answer', { answer: { text, approved: true,
                  _summary: text.slice(0, 90) } }); setText(''); }}
                  disabled={busy || !text.trim()}
                  className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-300 disabled:opacity-30">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </>
          )}

          {ask.kind === 'chat' && (
            <div className="flex gap-2">
              {/* textarea, not input: Boss pastes multi-paragraph briefs (e.g. a
                  whole prior conversation) — a single-line input silently strips
                  the newlines. Enter sends, Shift+Enter = new line, same contract
                  as the Brief tab's IntakeChat. */}
              <textarea value={text} onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && text.trim()) { e.preventDefault(); post('answer', { answer: { text, _summary: text.slice(0, 90) } }); setText(''); } }}
                placeholder="Type your answer… (Shift+Enter for a new line — long pastes welcome)"
                rows={Math.min(10, Math.max(2, text.split('\n').length))}
                className="flex-1 bg-black/40 rounded-lg px-3 py-2 text-sm text-gray-100 resize-y
                           ring-1 ring-white/10 focus:outline-none focus:ring-amber-500/40" />
              <button onClick={() => { post('answer', { answer: { text, _summary: text.slice(0, 90) } }); setText(''); }}
                disabled={busy || !text.trim()}
                className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-300 disabled:opacity-30">
                <Send className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Every waiting question gets the chat — gates, declares, reviews,
              all of them. Deciding blind or shutting the app down were the
              only outs before. */}
          <AskChat slug={slug} path={path} ask={ask} />
        </div>
      )}

      {st?.state === 'failed' && (
        <div className="flex items-start gap-2 rounded-xl bg-red-500/10 ring-1 ring-red-500/25 p-3">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-red-200">{st.error}</div>
        </div>
      )}

      {/* ── HISTORY — every input and output, forever ───── */}
      <div className="rounded-xl bg-black/30 ring-1 ring-white/10 overflow-hidden">
        <button onClick={() => setShowHist(v => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 text-[11px] uppercase
                     tracking-wider text-gray-500 hover:text-gray-300">
          <HistoryIcon className="h-3.5 w-3.5" />
          History — {hist.length} events{showHist ? '' : ' (show)'}
        </button>
        {showHist && (
          <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5 border-t border-white/5">
            {hist.length === 0 && <div className="px-3 py-3 text-xs text-gray-600">Nothing yet.</div>}
            {[...hist].reverse().map((e, i) => (
              <div key={i} className="px-3 py-1.5 flex items-start gap-2 text-[11px]">
                <span className="text-gray-700 tabular-nums shrink-0">{fmtLocal(e.ts)}</span>
                <span className={`shrink-0 w-3 ${COLOR[e.type] || 'text-gray-600'}`}>{ICON[e.type] || '·'}</span>
                <span className="text-gray-600 shrink-0 w-16 truncate">{e.step || '—'}</span>
                <span className={`${COLOR[e.type] || 'text-gray-400'} break-words`}>{e.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
